import { Plus, Camera, AlertTriangle } from "lucide-react";
import { addDays, daysBetween, dueText, deliveryText, projectLabel, todayISO, warnOf, OPEN } from "@/lib/orders";
import { formatCZK } from "@/lib/format";
import { ModeIcon } from "./OrderBits";

const LONG = new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Prague" });
const pasCount = (o) => (o.lines || []).filter((l) => l.pasport_item_id).length;
const items4 = (n) => `${n} ${n === 1 ? "položka" : n <= 4 ? "položky" : "položek"} z pasportu`;

function Row({ o, today, onOpen, sub, when }) {
  const w = warnOf(o, today);
  return (
    <button type="button" onClick={() => onOpen(o)} data-testid={`m-order-${o.id}`}
      className="w-full flex items-center gap-3 px-3.5 py-3 text-left border-t border-border/70 first:border-t-0 active:bg-secondary/60">
      <span className="w-10 h-10 rounded-full bg-secondary grid place-items-center shrink-0"><ModeIcon o={o} size={18} /></span>
      <span className="flex-1 min-w-0">
        <b className="block text-[15px] font-bold truncate">{o.supplier_name} · {o.title}</b>
        <span className="block text-[13px] text-muted-foreground truncate">{projectLabel(o)} · {sub ?? deliveryText(o)}</span>
      </span>
      <span className={`text-[13px] font-bold whitespace-nowrap ${w?.tone === "amber" ? "text-amber-700 dark:text-amber-400" : ""}`}>{when ?? (w?.text || dueText(o, today))}</span>
    </button>
  );
}

// Dodávky na telefonu (311, varianta C): co dnes přijde s tlačítkem na fotku, co hoří,
// Rozpracované / Vyřízené a filtr zakázek. Stavba tu jen přebírá; kancelář navíc zakládá.
export function DeliveriesMobile({ open, done, tab, onTab, projects, project, onProject, canEdit, canReceive, money, onOpen, onReceive, onNew, loading }) {
  const today = todayISO();
  const tomorrow = addDays(today, 1);
  const isToday = (o) => o.due_date === today && OPEN.includes(o.status);
  const hero = open.filter((o) => isToday(o) && !o.late);
  // Doplnit termín je práce kanceláře — stavbě se ukáže až s termínem.
  const fire = open.filter((o) => o.bucket === "now" && !hero.includes(o) && (canEdit || !(o.no_term && !o.late && !o.problem)));
  const rest = open.filter((o) => !hero.includes(o) && !fire.includes(o));
  const groups = [
    ["Zítra", rest.filter((o) => o.status !== "ready" && o.status !== "received" && o.due_date === tomorrow)],
    ["Tento týden", rest.filter((o) => o.status !== "ready" && o.status !== "received" && o.due_date > tomorrow && daysBetween(today, o.due_date) < 7)],
    ["K vyzvednutí", rest.filter((o) => o.status === "ready")],
    ["Později", rest.filter((o) => OPEN.includes(o.status) && o.status !== "ready" && (!o.due_date || daysBetween(today, o.due_date) >= 7))],
    ["Převzato", rest.filter((o) => o.status === "received")],
  ].filter(([, l]) => l.length).map(([t, l]) => [t, [...l].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))]);
  const sorted = (l) => [...l].sort((a, b) => (b.received?.at || "").localeCompare(a.received?.at || ""));

  return (
    <div className="space-y-4 pb-28" data-testid="deliveries-mobile">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-black tracking-tighter uppercase">{canEdit ? "Objednávky" : "Dodávky"}</h1>
          <p className="text-muted-foreground text-[14px] mt-0.5 first-letter:uppercase">{LONG.format(new Date())}{canEdit ? "" : " · tvoje stavby"}</p>
        </div>
        {canEdit && (
          <button type="button" onClick={onNew} aria-label="Nová objednávka" data-testid="m-order-new"
            className="w-11 h-11 rounded-full bg-brand text-white grid place-items-center shrink-0 shadow-sm"><Plus size={22} /></button>
        )}
      </div>

      {hero.length > 0 ? (
        <section className="rounded-[24px] bg-[#17181b] text-white dark:bg-secondary dark:text-foreground p-4 space-y-3" data-testid="m-today">
          <div>
            <div className="font-heading font-extrabold text-[30px] leading-none tabular-nums">{hero.length}</div>
            <div className="text-[14px] opacity-75 mt-1">dnes přijde nebo je k vyzvednutí</div>
          </div>
          {hero.map((o) => {
            const pickup = o.status === "ready" || o.delivery?.mode === "pickup";
            const pc = pasCount(o);
            return (
              <div key={o.id} className="rounded-[18px] bg-white/10 dark:bg-card p-3.5" data-testid={`m-today-${o.id}`}>
                <button type="button" onClick={() => onOpen(o)} className="w-full text-left">
                  <span className="flex items-center gap-1.5 text-[12.5px] opacity-75"><ModeIcon o={o} size={14} />
                    {pickup ? `k vyzvednutí · ${o.delivery?.branch || "pobočka"}` : `přiveze ${o.delivery?.carrier || "dopravce"} · ${projectLabel(o)}`}</span>
                  <b className="block text-[16px] font-bold mt-0.5">{o.supplier_name} · {o.title}</b>
                  <span className="block text-[13px] opacity-75">{[pc ? items4(pc) : null, o.due_note].filter(Boolean).join(" · ") || projectLabel(o)}</span>
                </button>
                {pickup && money && o.amount_gross != null && !o.paid && (
                  <div className="flex items-baseline justify-between mt-2 text-[13px]">
                    <span className="opacity-75">K úhradě na pobočce</span><b className="tabular-nums text-[15px]">{formatCZK(o.amount_gross)} <span className="font-normal opacity-75">s DPH</span></b>
                  </div>
                )}
                {canReceive && (
                  <button type="button" onClick={() => onReceive(o)} data-testid={`m-receive-${o.id}`}
                    className="mt-3 w-full h-12 rounded-[14px] bg-brand text-white font-bold inline-flex items-center justify-center gap-2">
                    <Camera size={18} />{pickup ? "Vyzvednuto — přidat doklad" : "Přišlo — přidat doklad"}
                  </button>
                )}
              </div>
            );
          })}
        </section>
      ) : !loading && (
        <section className="glass rounded-[20px] px-4 py-3.5 text-[14px] text-muted-foreground" data-testid="m-today-empty">Dnes nic nepřijde.</section>
      )}

      {fire.length > 0 && (
        <section className="rounded-[20px] border border-destructive/40 bg-destructive/[.06] overflow-hidden" data-testid="m-fire">
          <div className="flex items-center gap-2 px-3.5 pt-3 text-destructive font-bold text-[14px]"><AlertTriangle size={16} />Řešit {fire.length}</div>
          {fire.map((o) => (
            <button key={o.id} type="button" onClick={() => onOpen(o)} className="w-full text-left px-3.5 py-2.5 border-t border-destructive/15 first-of-type:border-t-0">
              <b className="block text-[14.5px] font-bold truncate">{o.supplier_name} · {o.title}</b>
              <span className="block text-[13px]"><span className="text-destructive font-semibold">{warnOf(o, today)?.text}</span> · {projectLabel(o)}</span>
            </button>
          ))}
          {!canEdit && <p className="px-3.5 pb-3 text-[12.5px] text-muted-foreground">Kancelář o tom ví.</p>}
        </section>
      )}

      <div className="flex h-11 p-1 rounded-full bg-secondary" role="tablist" data-testid="m-tabs">
        {[["open", "Rozpracované", open.length], ["done", "Vyřízené", done?.length]].map(([k, t, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => onTab(k)} data-testid={`m-tab-${k}`}
            className={`flex-1 rounded-full text-[14px] ${tab === k ? "bg-card font-bold shadow-sm" : "font-semibold text-muted-foreground"}`}>
            {t}{n != null ? ` ${n}` : ""}
          </button>
        ))}
      </div>

      {projects.length > 1 && (
        <div className="-mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar" data-testid="m-chips">
          {[{ value: "", label: "Všechny zakázky" }, ...projects].map((p) => (
            <button key={p.value || "all"} type="button" onClick={() => onProject(p.value)} data-testid={`m-chip-${p.value || "all"}`}
              className={`h-9 px-3.5 rounded-full text-[13px] font-semibold whitespace-nowrap shrink-0 ${project === p.value ? "bg-foreground text-background" : "bg-card border border-border"}`}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {tab === "open" ? (
        groups.length ? groups.map(([title, list]) => (
          <section key={title}>
            <h2 className="px-1 pb-1.5 text-[13px] font-extrabold uppercase tracking-wide text-muted-foreground">{title}</h2>
            <div className="glass rounded-[20px] overflow-hidden">
              {list.map((o) => <Row key={o.id} o={o} today={today} onOpen={onOpen}
                when={title === "Převzato" ? (o.paid ? "zaplaceno" : "čeká na platbu") : undefined} />)}
            </div>
          </section>
        )) : !loading && <p className="text-center text-muted-foreground py-6">{project ? "Na téhle zakázce teď nic dalšího nepřijde." : "Nic dalšího teď nepřijde."}</p>
      ) : (
        done?.length ? (
          <div className="glass rounded-[20px] overflow-hidden" data-testid="m-done">
            {sorted(done).map((o) => <Row key={o.id} o={o} today={today} onOpen={onOpen} when=""
              sub={`${dueText(o, today)} · zaplaceno`} />)}
          </div>
        ) : !loading && <p className="text-center text-muted-foreground py-6">{project ? "Na téhle zakázce zatím nic vyřízeného." : "Zatím nic vyřízeného."}</p>
      )}
    </div>
  );
}
